import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Ansi from 'ansi-to-react';
import './ServerConsole.css';

interface Server {
  id: string;
  name: string;
  type: string;
  game: string;
  version: string;
  status: 'online' | 'offline' | 'maintenance';
  players: number;
  maxPlayers: number;
  active?: boolean;
  // ip is intentionally NOT typed here — backend no longer exposes the
  // raw VPS IP. Replaced with `provisioned` for the "has been started"
  // gating that the console needs.
  provisioned?: boolean;
}

export function ServerConsole() {
  const { id } = useParams<{ id: string }>();

  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Console/Logs state
  const [logs, setLogs] = useState<string[]>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const [commandInput, setCommandInput] = useState('');
  const [sendingCommand, setSendingCommand] = useState(false);

  // Fetch server info
  useEffect(() => {
    const fetchServer = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers`, { credentials: 'include' });
        const data = await res.json();

        if (!res.ok) {
          setError(data.error || 'Error al cargar servidor');
          return;
        }

        const foundServer = data.servers?.find((s: Server) => String(s.id) === String(id));
        if (!foundServer) {
          setError('Servidor no encontrado');
          return;
        }

        setServer(foundServer);
      } catch (err) {
        console.log(err);
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchServer();
    }
  }, [id]);

  // Connect to logs stream
  const connectToLogs = useCallback(async () => {
    if (!server?.active || !server.provisioned) return;

    if (eventSource) {
      eventSource.close();
    }

    setLogs([]);

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/logs`, {
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        console.error('Error getting logs URL:', data.error);
        return;
      }

      const data = await res.json();
      const logsUrl = data.logsUrl;

      if (!logsUrl) {
        console.error('No logs URL returned');
        return;
      }

      const source = new EventSource(logsUrl);

      source.onmessage = (event) => {
        setLogs(prev => [...prev.slice(-500), event.data]);
      };

      source.onerror = (error) => {
        console.error('SSE Error:', error);
        source.close();
      };

      setEventSource(source);
    } catch (err) {
      console.error('Error connecting to logs:', err);
    }
  }, [server, id, eventSource]);

  // Disconnect from logs stream
  const disconnectFromLogs = useCallback(() => {
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
  }, [eventSource]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Connect to logs when server is active
  useEffect(() => {
    if (server?.active && !eventSource) {
      connectToLogs();
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [server?.active]);

  // Send command to server
  const sendCommand = useCallback(async (command: string) => {
    if (!command.trim() || sendingCommand || !server?.active) return;

    // Add command to logs immediately BEFORE sending
    setLogs(prev => [...prev.slice(-500), `> ${command}`]);

    setSendingCommand(true);

    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/servers/${id}/command`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command })
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || 'Error al enviar comando');
      } else {
        setCommandInput('');
      }
    } catch (err) {
      console.error('Error sending command:', err);
      window.location.reload();
    } finally {
      setSendingCommand(false);
    }
  }, [id, server, sendingCommand]);

  // Handle loading state
  if (loading) {
    return (
      <div className="console-fullscreen">
        <div className="console-loading">
          <div className="spinner"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !server) {
    return (
      <div className="console-fullscreen">
        <div className="console-error">
          <p>{error || 'Error desconocido'}</p>
          <Link to="/dashboard" className="console-back-link">Volver al Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="console-fullscreen">
      {/* Terminal Header */}
      <div className="console-header console-header-full">
        <div className="console-title">
          <div className="terminal-controls">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
          <span className="terminal-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="4 17 10 11 4 5"></polyline>
              <line x1="12" y1="19" x2="20" y2="19"></line>
            </svg>
          </span>
          <h3>Terminal — {server.name}</h3>
        </div>

        <div className="console-status">
          <div className={`status-indicator ${server.active ? 'connected' : 'disconnected'}`}>
            <span className="status-dot"></span>
            {server.active ? 'Online' : 'Offline'}
          </div>
          <Link to={`/server/${id}`} className="console-back-btn">
            ← Volver
          </Link>
        </div>
      </div>

      {/* Logs Container */}
      <div className="console-logs-full" ref={logsContainerRef}>
        {!server.active ? (
          <div className="logs-empty">
            <div className="waiting-icon">💻</div>
            <p>El servidor está apagado</p>
            <p style={{ color: '#666', fontSize: '0.85rem' }}>Enciende el servidor para ver los logs</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="logs-empty">
            <div className="waiting-icon">💻</div>
            <p>Conectando al stream de logs...</p>
          </div>
        ) : (
          <>
            <pre className="logs-output">
              {logs.map((log, index) => (
                <LogLine key={index} content={log} game={server.game} />
              ))}
            </pre>
          </>
        )}
      </div>

      {/* Command Input */}
      <div className="command-input-container command-input-full">
        <span className="command-prompt">&gt;</span>
        <input
          type="text"
          className="command-input"
          placeholder={server.active ? 'Escribe un comando...' : 'El servidor debe estar activo'}
          value={commandInput}
          onChange={(e) => setCommandInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !sendingCommand && server.active) {
              sendCommand(commandInput);
            }
          }}
          disabled={!server.active || sendingCommand}
        />
        <button
          className="send-command-btn"
          onClick={() => sendCommand(commandInput)}
          disabled={!server.active || sendingCommand || !commandInput.trim()}
        >
          {sendingCommand ? '⏳' : '➤'}
        </button>
      </div>
    </div>
  );
}

// Log line component with syntax highlighting
function LogLine({ content, game }: { content: string; game?: string }) {
  // Use useMemo for performance if this were a very large list, 
  // but for log lines it's usually fine as-is.
  
  const cleanContent = (() => {
    if (!content) return "";
    
    let text = content;

    // Remove Terminal Title sequences (OSC 0) which are common in Terraria/TShock
    // These look like: \x1b]0;Title\x07 or \x1b]0;Title\x1b\
    // The image shows broken versions like "]]0;..." or "]0;..."
    
    // 1. Standard OSC Title
    text = text.replace(/\x1b\]0;.*?(?:\x07|\x1b\\)/g, '');
    
    // 2. Broken OSC (common in some Docker/SSH streams)
    text = text.replace(/\]\]0;.*?(?:\x07|\x1b\\|$)/g, '');
    
    // 3. Specifically for Terraria TShock which sends very noisy title updates
    if (game?.toLowerCase().includes('terraria')) {
      // Strip things like "1/8 on LuxoWorld @ 0.0.0.0:7777" which are title bar updates
      text = text.replace(/.*?\d+\/\d+\s+on\s+.*?\s+@\s+.*?\).*?/g, '');
      
      // Also some literal broken escape sequences that appear
      text = text.replace(/\]0;.*?\).*?(?:\s|$)/g, '');
      
      // Remove any remaining fragments like "1/8 on LuxoWorld"
      text = text.replace(/\d+\/\d+\s+on\s+.*?\)/g, '');
    }

    return text.trim();
  })();

  const getLogClass = (text: string): string => {
    const lowerText = text.toLowerCase();

    // Command (starts with > )
    if (text.startsWith('> ')) {
      return 'log-command';
    }

    // Success / Broadcast patterns
    if (lowerText.includes('success') || lowerText.includes('done') || lowerText.includes('started') || 
        lowerText.includes('ready') || lowerText.includes('broadcast') || lowerText.includes('(server)')) {
      return 'log-success';
    }

    // Error patterns
    if (lowerText.includes('error') || lowerText.includes('exception') || lowerText.includes('fatal') || 
        lowerText.includes('failed') || lowerText.includes('critical')) {
      return 'log-error';
    }

    // Warning patterns
    if (lowerText.includes('warn') || lowerText.includes('warning') || lowerText.includes('caution')) {
      return 'log-warn';
    }

    // Debug patterns
    if (lowerText.includes('debug') || lowerText.includes('trace')) {
      return 'log-debug';
    }

    // Info patterns
    if (lowerText.includes('info') || lowerText.includes('loading') || lowerText.includes('connecting')) {
      return 'log-info';
    }

    return '';
  };

  if (!cleanContent) return null;

  const logClass = getLogClass(cleanContent);

  return (
    <div className={`log-line ${logClass}`}>
      <Ansi>{cleanContent}</Ansi>
    </div>
  );
}
